import React from 'react';
import { PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { Client, FamilyMember } from '../../types';
import { Button, Card } from '../ui';
import { FamilyMemberCard } from './FamilyMemberCard';
import { groupFamilyMembers, getRelationshipLabel } from './clientProfileUtils';

const GROUP_LABELS: Record<string, { title: string; bar: string }> = {
  baseFamily: { title: 'Familia base', bar: 'bg-indigo-500' },
  hijos: { title: 'Hijos', bar: 'bg-sky-500' },
  hermanos: { title: 'Hermanos', bar: 'bg-teal-500' },
  suegros: { title: 'Suegros', bar: 'bg-amber-500' },
  otros: { title: 'Otros', bar: 'bg-slate-400' }
};

export interface ClientFamilySectionProps {
  client: Client;
  totalCount: number;
  grouped: ReturnType<typeof groupFamilyMembers>;
  onAdd: () => void;
  onEdit: (fm: FamilyMember) => void;
  onDelete: (id: string) => void;
  calculateAge: (dob: string) => number;
  compact?: boolean;
  className?: string;
}

export const ClientFamilySection: React.FC<ClientFamilySectionProps> = ({
  client,
  totalCount,
  grouped,
  onAdd,
  onEdit,
  onDelete,
  calculateAge,
  compact = false,
  className
}) => {
  const order: (keyof typeof grouped)[] = ['baseFamily', 'hijos', 'hermanos', 'suegros', 'otros'];

  return (
    <Card className={clsx('overflow-hidden border-slate-200/90 p-0 shadow-sm ring-1 ring-slate-100/80', className)}>
      <div className="flex flex-col gap-3 border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 ring-1 ring-emerald-200/80">
            <UserGroupIcon className="h-5 w-5 text-emerald-700" aria-hidden />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900">Familiares</h3>
            <p className="text-xs text-slate-500">
              {totalCount === 0
                ? 'Nadie registrado aún en el padrón familiar'
                : `${totalCount} persona(s) vinculada(s) a ${client.name.split(' ')[0]}`}
            </p>
          </div>
        </div>
        <Button
          type="button"
          onClick={onAdd}
          variant="danger"
          size="sm"
          className="shrink-0 bg-slate-900 hover:bg-slate-800"
        >
          <PlusIcon className="mr-1.5 h-4 w-4" />
          Agregar familiar
        </Button>
      </div>

      <div className={clsx('p-5', compact && 'max-h-[min(55vh,480px)] overflow-y-auto pr-2')}>
        {totalCount === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-12 text-center">
            <UserGroupIcon className="mx-auto h-12 w-12 text-slate-200" aria-hidden />
            <p className="mt-3 font-medium text-slate-600">No hay familiares registrados</p>
            <Button type="button" variant="outline" size="sm" className="mt-4" onClick={onAdd}>
              Registrar el primero
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {order.map((key) => {
              const list = grouped[key];
              if (!list.length) return null;
              const meta = GROUP_LABELS[key];
              return (
                <div key={key}>
                  <h4 className="mb-3 flex items-center text-sm font-bold text-slate-800">
                    <span className={clsx('mr-2 h-5 w-1 rounded-full', meta.bar)} />
                    {meta.title}
                    <span className="ml-2 text-xs font-semibold text-slate-400">({list.length})</span>
                  </h4>
                  <div
                    className={clsx(
                      'grid gap-3',
                      compact ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
                    )}
                  >
                    {list.map((fm) => (
                      <FamilyMemberCard
                        key={fm.id}
                        familyMember={fm}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        getRelationshipLabel={getRelationshipLabel}
                        calculateAge={calculateAge}
                        compact={compact}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};
